import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useSupplierMitigationTasks } from '../../hooks/useSupplierMitigationTasks'
import { useSuppliers } from '../../hooks/useSuppliers'
import { useUserEarlySignalsInterests } from '../../hooks/useUserEarlySignalsInterests'
import {
  resolveMitigationCoordinates,
  resolveMitigationDisplayLocation,
} from '../../utils/regionToCoordinates'
import { mitigationMatchesRegionOrLocation } from './mitigationRegionFilter'
import { GeographicRiskMap } from './GeographicRiskMap'
import { MitigationDetailModal } from './MitigationDetailModal'
import { CopyTableButtons } from './CopyTableButtons'
import { cell } from './copyTableToClipboard'
import type { SupplierMitigationTask } from '../../types'
import { toErrorMessage } from '../../utils/toErrorMessage'
import { EsBadge, EsPageHeader } from './ui'
import { riskBand, supplierDisplayName, supplierGroupIds, type RiskBand } from './supplierRollup'
import {
  type ProcurementTypeFilter,
  loadPortfolioFiltersFromStorage,
  matchesProcurementTypeFilter,
  savePortfolioFiltersToStorage,
} from './esPortfolioFilters'
import { ProcurementTypeSelect } from './ProcurementTypeSelect'

const MITIGATION_HEADERS = ['Supplier', 'Task', 'Date', 'Location', 'Status'] as const

type StatusFilter = 'active' | 'open'
type DateWindow = 'ltm' | 'all'
type SeverityFilter = '' | 'critical' | 'high' | 'medium' | 'low'
type SupplierRiskFilter = 'all' | RiskBand | 'unscored'

function isActiveMitigation(t: SupplierMitigationTask): boolean {
  return t.status !== 'closed' && t.status !== 'completed'
}

function isOpenMitigation(t: SupplierMitigationTask): boolean {
  return t.status === 'open' || t.status === 'in_progress' || t.status === 'at_risk'
}

function mitigationEventDate(t: SupplierMitigationTask): Date | null {
  const raw = t.alert_date || t.created_at
  if (!raw) return null
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

function formatMonthYear(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
}

function ltmWindow(now = new Date()): { start: Date; end: Date; label: string } {
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  const start = new Date(end)
  start.setFullYear(start.getFullYear() - 1)
  start.setHours(0, 0, 0, 0)
  return {
    start,
    end,
    label: `Last 12 months: ${formatMonthYear(start)} – ${formatMonthYear(end)}`,
  }
}

function taskSeverityKey(t: SupplierMitigationTask): SeverityFilter {
  const raw = (t.severity || t.priority || '').toLowerCase()
  if (raw === 'critical') return 'critical'
  if (raw === 'high') return 'high'
  if (raw === 'medium' || raw === 'moderate') return 'medium'
  if (raw === 'low') return 'low'
  return ''
}

function supplierRiskKey(overall: number | null | undefined): SupplierRiskFilter {
  if (overall == null || Number.isNaN(Number(overall))) return 'unscored'
  return riskBand(Number(overall))
}

export function DashboardView() {
  const { isAdmin } = useAuth()
  const { suppliers } = useSuppliers(isAdmin ? { scope: 'all' } : undefined)
  const { tasks: mitigationTasks, loading, error } = useSupplierMitigationTasks()
  const { interests } = useUserEarlySignalsInterests()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedTask, setSelectedTask] = useState<SupplierMitigationTask | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active')
  const [dateWindow, setDateWindow] = useState<DateWindow>('ltm')
  const [regionFilter, setRegionFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('')
  const [countryFilter, setCountryFilter] = useState('')
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('')
  const [supplierRiskFilter, setSupplierRiskFilter] = useState<SupplierRiskFilter>('all')
  const [procurementTypeFilter, setProcurementTypeFilter] = useState<ProcurementTypeFilter>(
    () => loadPortfolioFiltersFromStorage().procurementType
  )

  const period = useMemo(() => ltmWindow(), [])

  const supplierById = useMemo(() => {
    const m = new Map(suppliers.map((s) => [s.id, s]))
    return m
  }, [suppliers])

  /** Deep-link from Overview: /dashboard?supplier=<id> */
  useEffect(() => {
    const id = searchParams.get('supplier')?.trim()
    if (id) setSupplierFilter(id)
  }, [searchParams])

  /** Expand selected product-line id to full portfolio-collapsed group. */
  const supplierFilterIds = useMemo(() => {
    if (!supplierFilter) return null
    return new Set(supplierGroupIds(supplierFilter, suppliers, procurementTypeFilter))
  }, [supplierFilter, suppliers, procurementTypeFilter])

  const tasksInScope = useMemo(() => {
    return mitigationTasks.filter((t) => {
      if (!isActiveMitigation(t)) return false
      const supplier = t.supplier_id ? supplierById.get(t.supplier_id) : null
      if (!matchesProcurementTypeFilter(supplier?.procurement_type, procurementTypeFilter)) {
        return false
      }
      if (dateWindow === 'all') return true
      const eventDate = mitigationEventDate(t)
      if (!eventDate || eventDate < period.start || eventDate > period.end) return false
      return true
    })
  }, [mitigationTasks, period, dateWindow, supplierById, procurementTypeFilter])

  const regions = useMemo(() => {
    const set = new Set<string>()
    for (const t of tasksInScope) {
      const s = t.supplier_id ? supplierById.get(t.supplier_id) : null
      const r = (s?.kenvue_region || '').trim()
      if (r && r !== 'Not Available') set.add(r)
    }
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [tasksInScope, supplierById])

  const categories = useMemo(() => {
    const set = new Set<string>()
    for (const t of tasksInScope) {
      const s = t.supplier_id ? supplierById.get(t.supplier_id) : null
      const cat = (s?.category || s?.industry || '').trim()
      if (cat) set.add(cat)
    }
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [tasksInScope, supplierById])

  const countries = useMemo(() => {
    const set = new Set<string>()
    for (const t of tasksInScope) {
      const s = t.supplier_id ? supplierById.get(t.supplier_id) : null
      if (regionFilter && (s?.kenvue_region || '') !== regionFilter) continue
      const loc = (s?.location || '').trim()
      if (loc && loc !== 'Not Available') set.add(loc)
    }
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [tasksInScope, supplierById, regionFilter])

  const supplierOptions = useMemo(() => {
    const byId = new Map<string, string>()
    for (const t of tasksInScope) {
      if (!t.supplier_id) continue
      const s = supplierById.get(t.supplier_id)
      if (!s) continue
      const name = supplierDisplayName(s)
      if (name) byId.set(t.supplier_id, name)
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [tasksInScope, supplierById])

  useEffect(() => {
    if (categoryFilter && !categories.includes(categoryFilter)) setCategoryFilter('')
  }, [categories, categoryFilter])

  useEffect(() => {
    if (countryFilter && !countries.includes(countryFilter)) setCountryFilter('')
  }, [countries, countryFilter])

  useEffect(() => {
    if (regionFilter && !regions.includes(regionFilter)) setRegionFilter('')
  }, [regions, regionFilter])

  const activeMitigations = useMemo(() => {
    let list = tasksInScope.filter((t) => {
      if (statusFilter === 'open' && !isOpenMitigation(t)) return false
      const supplier = t.supplier_id ? supplierById.get(t.supplier_id) : null
      if (supplierFilterIds && (!t.supplier_id || !supplierFilterIds.has(t.supplier_id))) return false
      if (categoryFilter) {
        const cat = supplier?.category || supplier?.industry || ''
        if (cat !== categoryFilter) return false
      }
      if (regionFilter) {
        if ((supplier?.kenvue_region || '') !== regionFilter) return false
      }
      if (countryFilter) {
        if ((supplier?.location || '') !== countryFilter) return false
      }
      if (severityFilter) {
        if (taskSeverityKey(t) !== severityFilter) return false
      }
      if (supplierRiskFilter !== 'all') {
        if (supplierRiskKey(supplier?.overall_score) !== supplierRiskFilter) return false
      }
      return true
    })

    if (interests) {
      const { followed_supplier_ids, regions: interestRegions } = interests
      const hasSupplierFilter = followed_supplier_ids?.length > 0
      const hasRegionFilter = interestRegions?.length > 0
      if (hasSupplierFilter || hasRegionFilter) {
        list = list.filter((t) => {
          const matchSupplier =
            !hasSupplierFilter || (t.supplier_id && followed_supplier_ids!.includes(t.supplier_id))
          const supplier = t.supplier_id ? supplierById.get(t.supplier_id) : null
          const matchRegion =
            !hasRegionFilter ||
            interestRegions!.some((r) => mitigationMatchesRegionOrLocation(t, supplier, r))
          return matchSupplier && matchRegion
        })
      }
    }

    return list
  }, [
    tasksInScope,
    interests,
    supplierById,
    statusFilter,
    regionFilter,
    categoryFilter,
    supplierFilterIds,
    countryFilter,
    severityFilter,
    supplierRiskFilter,
  ])

  const mitigationsSorted = useMemo(() => {
    return [...activeMitigations].sort((a, b) => {
      const dateA = a.alert_date || a.created_at || ''
      const dateB = b.alert_date || b.created_at || ''
      return dateB.localeCompare(dateA)
    })
  }, [activeMitigations])

  const itemsWithCoords = useMemo(() => {
    const withResolved: Array<
      SupplierMitigationTask & { lat: number; lng: number; displayLocation: string | null }
    > = []
    for (const t of activeMitigations) {
      const supplier = t.supplier_id ? supplierById.get(t.supplier_id) : undefined
      const coords = resolveMitigationCoordinates(t, supplier, { jitter: true, id: t.id })
      if (coords) {
        withResolved.push({
          ...t,
          lat: coords.lat,
          lng: coords.lng,
          displayLocation: resolveMitigationDisplayLocation(t, supplier),
        })
      }
    }
    return withResolved
  }, [activeMitigations, supplierById])

  const openCount = useMemo(
    () => activeMitigations.filter(isOpenMitigation).length,
    [activeMitigations]
  )

  const mitigationRowsAll = useMemo(() => {
    return mitigationsSorted.map((t) => {
      const supplier = t.supplier_id ? supplierById.get(t.supplier_id) : null
      const location = resolveMitigationDisplayLocation(t, supplier)
      const dateRaw = t.alert_date || t.created_at || ''
      const displayDate = dateRaw
        ? new Date(dateRaw).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : ''
      return [
        cell(supplier ? supplierDisplayName(supplier) : ''),
        cell(t.task),
        cell(displayDate),
        cell(location),
        cell(t.status),
      ]
    })
  }, [mitigationsSorted, supplierById])

  const selectClass =
    'rounded-[var(--radius-sm)] border border-es-hairline px-2.5 py-2 text-sm bg-es-white text-es-body focus:border-es-signal focus:outline-none focus:ring-1 focus:ring-es-signal'

  const clearPickerFilters = () => {
    setCategoryFilter('')
    setSupplierFilter('')
    setCountryFilter('')
    setRegionFilter('')
    setSeverityFilter('')
    setSupplierRiskFilter('all')
    setStatusFilter('active')
    setDateWindow('ltm')
    if (searchParams.has('supplier')) {
      const next = new URLSearchParams(searchParams)
      next.delete('supplier')
      setSearchParams(next, { replace: true })
    }
  }

  const hasPickerFilters =
    !!categoryFilter ||
    !!supplierFilter ||
    !!countryFilter ||
    !!regionFilter ||
    !!severityFilter ||
    supplierRiskFilter !== 'all' ||
    statusFilter !== 'active' ||
    dateWindow !== 'ltm'

  const periodLabel = dateWindow === 'ltm' ? period.label : 'All dates (active mitigations)'

  return (
    <div className="p-4 md:p-6 space-y-6">
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" role="alert">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden />
          {toErrorMessage(error, 'Failed to load mitigations.')}
        </div>
      )}
      <div className="flex flex-wrap gap-4 items-end justify-between">
        <EsPageHeader
          className="mb-0"
          title="Open mitigation actions"
          description="Mitigation activity only: map and list of open actions by geography and status. Portfolio risk insights stay on Risk overview; supplier deep-dives on Intelligence view."
          eyebrow="Risk & mitigation monitor"
        />
        <EsBadge tone="quiet">
          Open mitigations: <strong>{activeMitigations.length}</strong>
        </EsBadge>
      </div>

      <ProcurementTypeSelect
        value={procurementTypeFilter}
        onChange={(next) => {
          setProcurementTypeFilter(next)
          savePortfolioFiltersToStorage({
            ...loadPortfolioFiltersFromStorage(),
            procurementType: next,
          })
        }}
      />

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className={selectClass}
          aria-label="Mitigation status"
        >
          <option value="active">Status: All active</option>
          <option value="open">Status: Open only</option>
        </select>
        <select
          value={dateWindow}
          onChange={(e) => setDateWindow(e.target.value as DateWindow)}
          className={selectClass}
          aria-label="Date window"
        >
          <option value="ltm">Date: Last 12 months</option>
          <option value="all">Date: All active</option>
        </select>
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
          className={selectClass}
          aria-label="Severity"
        >
          <option value="">Severity</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select
          value={supplierRiskFilter}
          onChange={(e) => setSupplierRiskFilter(e.target.value as SupplierRiskFilter)}
          className={selectClass}
          aria-label="Supplier risk level"
        >
          <option value="all">Supplier risk</option>
          <option value="high">High risk suppliers</option>
          <option value="medium">Medium risk suppliers</option>
          <option value="low">Low risk suppliers</option>
          <option value="unscored">Unscored suppliers</option>
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className={selectClass}
          aria-label="Category"
        >
          <option value="">Category</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={supplierFilter}
          onChange={(e) => {
            const id = e.target.value
            setSupplierFilter(id)
            const next = new URLSearchParams(searchParams)
            if (id) next.set('supplier', id)
            else next.delete('supplier')
            setSearchParams(next, { replace: true })
          }}
          className={`${selectClass} max-w-[220px]`}
          aria-label="Supplier"
        >
          <option value="">Supplier</option>
          {supplierFilter && !supplierOptions.some((o) => o.id === supplierFilter) && (
            <option value={supplierFilter}>
              {(() => {
                const s = supplierById.get(supplierFilter)
                return s ? supplierDisplayName(s) : 'Selected supplier'
              })()}
            </option>
          )}
          {supplierOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <select
          value={regionFilter}
          onChange={(e) => {
            setRegionFilter(e.target.value)
            setCountryFilter('')
          }}
          className={selectClass}
          aria-label="Region"
        >
          <option value="">Region</option>
          {regions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          className={selectClass}
          aria-label="Country"
        >
          <option value="">Country</option>
          {countries.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <span className="text-xs text-es-muted">{periodLabel}</span>
        {hasPickerFilters && (
          <button
            type="button"
            onClick={clearPickerFilters}
            className="text-xs font-semibold text-es-signal hover:text-es-deep hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <GeographicRiskMap
            items={itemsWithCoords.map((t) => ({
              id: t.id,
              lat: t.lat,
              lng: t.lng,
              headline: t.task,
              region: t.displayLocation,
              severity: t.severity || t.priority || null,
            }))}
            onSelectItem={(item) => {
              const full = activeMitigations.find((t) => t.id === item.id)
              setSelectedTask(full ?? null)
            }}
            title="Open mitigation actions"
            subtitle={
              loading
                ? 'Resolving coordinates…'
                : `${periodLabel} · ${itemsWithCoords.length} of ${activeMitigations.length} open mitigation actions with coordinates`
            }
            loading={loading}
          />
        </div>
        <div className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] overflow-hidden flex flex-col">
          <div className="p-3 border-b border-es-soft flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-semibold text-es-navy">Open mitigation actions</h2>
              <p className="text-[11px] text-es-muted mt-0.5">{periodLabel}</p>
            </div>
            <CopyTableButtons
              disabled={loading || mitigationRowsAll.length === 0}
              payload={{ headers: [...MITIGATION_HEADERS], rows: mitigationRowsAll }}
            />
          </div>
          <div className="flex-1 overflow-y-auto max-h-[480px]">
            {loading ? (
              <div className="p-6 text-center text-es-muted">
                <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
                Loading...
              </div>
            ) : activeMitigations.length === 0 ? (
              <div className="p-6 text-center">
                {mitigationTasks.some(isActiveMitigation) ? (
                  <p className="text-es-body text-sm">
                    No mitigations match your filters for this period. Clear filters in Preferences or
                    broaden status/region filters.
                  </p>
                ) : (
                  <p className="text-es-muted">No open mitigation actions yet.</p>
                )}
              </div>
            ) : (
              <ul className="divide-y divide-es-soft">
                {mitigationsSorted.slice(0, 50).map((t) => {
                  const supplier = t.supplier_id ? supplierById.get(t.supplier_id) : null
                  const location = resolveMitigationDisplayLocation(t, supplier)
                  const displayDate = t.alert_date
                    ? new Date(t.alert_date).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })
                    : t.created_at
                      ? new Date(t.created_at).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : null
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedTask(t)}
                        className="w-full text-left p-3 hover:bg-es-haze transition-colors"
                      >
                        <span
                          className={`inline-block w-2 h-2 rounded-full mr-2 ${
                            (t.severity || t.priority || '').toLowerCase() === 'critical' ||
                            (t.severity || t.priority || '').toLowerCase() === 'high'
                              ? 'bg-es-riskHigh'
                              : (t.severity || t.priority || '').toLowerCase() === 'medium' ||
                                  (t.severity || t.priority || '').toLowerCase() === 'moderate'
                                ? 'bg-es-riskMedium'
                                : (t.severity || t.priority || '').toLowerCase() === 'low'
                                  ? 'bg-es-riskLow'
                                  : 'bg-es-signal'
                          }`}
                          aria-hidden
                        />
                        <span className="text-es-navy font-medium line-clamp-2">{t.task}</span>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
                          {displayDate && (
                            <span
                              className="text-xs text-es-muted"
                              title={t.alert_date || t.created_at || undefined}
                            >
                              {displayDate}
                            </span>
                          )}
                          {location && <span className="text-xs text-es-muted">{location}</span>}
                          {supplier && supplierDisplayName(supplier) && supplier.id && (
                            <Link
                              to={`/early-signals/risk-matrix?supplier=${encodeURIComponent(supplier.id)}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs px-1.5 py-0.5 rounded bg-es-soft text-es-signal hover:bg-es-haze hover:underline"
                              title="Open supplier in Intelligence view"
                            >
                              {supplierDisplayName(supplier)}
                            </Link>
                          )}
                          {supplier && supplierDisplayName(supplier) && !supplier.id && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-es-soft text-es-body">
                              {supplierDisplayName(supplier)}
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-0 h-24 bg-es-white border border-es-soft rounded-[var(--radius-lg)] overflow-hidden shadow-[var(--shadow-card)]">
        <div className="flex flex-col items-center justify-center border-r border-es-soft">
          <span className="text-3xl font-bold text-es-navy">{activeMitigations.length}</span>
          <span className="text-xs text-es-muted font-semibold uppercase tracking-wider mt-1">
            Open mitigations
          </span>
        </div>
        <div className="flex flex-col items-center justify-center border-r border-es-soft">
          <span className="text-3xl font-bold text-es-deep">{itemsWithCoords.length}</span>
          <span className="text-xs text-es-muted font-semibold uppercase tracking-wider mt-1">
            With coordinates
          </span>
        </div>
        <div className="flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-es-navy">{openCount}</span>
          <span className="text-xs text-es-muted font-semibold uppercase tracking-wider mt-1">Open</span>
        </div>
      </div>

      <MitigationDetailModal
        task={selectedTask}
        supplier={
          selectedTask?.supplier_id ? supplierById.get(selectedTask.supplier_id) ?? null : null
        }
        onClose={() => setSelectedTask(null)}
      />
    </div>
  )
}
